import { Link } from "react-router-dom";
import { SectionLabel, Headline, Stamp } from "@/components/Doc";

const ROWS = [
  ["Per location, per month", "$499", "$1,200", "$2,400"],
  ["Scored service interactions", "10 /mo", "10 /mo", "10 /mo"],
  ["Follow-through verification", "Yes", "Yes", "Yes"],
  ["Email & SMS simulation", "—", "2 campaigns /mo", "2 campaigns /mo"],
  ["Verification & report rates", "—", "Yes", "Yes"],
  ["Quarterly credential audit", "—", "Yes", "Yes"],
  ["Concurrency testing", "—", "—", "Yes"],
  ["Quarterly leadership debrief", "—", "—", "Yes"],
  ["Drift tracking", "—", "—", "Yes"],
  ["Named station officer", "—", "—", "Yes"],
  ["Annual continuity drill", "—", "—", "Yes"],
];

const INSTALLS = [
  ["INSTALL — SINGLE MODULE", "Two days on site, up to 20", "from $28,000", "memorandum"],
  ["INSTALL — THE FULL INSTALL", "Four modules across a quarter", "from $95,000", "memorandum"],
  ["DEALER GROUP INSTALL", "Per rooftop, minimum three", "from $6,500", "dealer-memorandum"],
  ["RETAINED ADVISORY", "Twelve-month term", "from $7,500 /mo", "memorandum"],
];

const Note = ({ children }) => <p className="t-body text-base text-olive italic leading-snug">{children}</p>;

export default function Pricing() {
  return (
    <main className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="pricing-page">
      <SectionLabel right="Form LH-30">Doc. 11 — Schedule of Fees</SectionLabel>
      <div className="relative mt-10">
        <div className="absolute -top-6 right-0 sm:right-10"><Stamp color="carbon" rotate={5} size="md">Schedule of Fees</Stamp></div>
        <Headline as="h1" className="text-4xl sm:text-5xl lg:text-6xl">Rate schedule.</Headline>
        <p className="t-body text-lg sm:text-xl mt-6 max-w-[58ch] leading-[1.65]">Three ways to engage the firm: continuously, by installation, or at the door. Billing for continuous work begins when testing begins, not when you purchase.</p>
      </div>

      <section className="mt-16" data-testid="pricing-section-a">
        <SectionLabel>Section A — Continuous (WATCH)</SectionLabel>
        <div className="mt-6 overflow-x-auto pt-6">
          <table className="w-full border-t border-b border-carbon text-left min-w-[640px]">
            <thead>
              <tr className="border-b border-carbon">
                <th className="py-4 pr-4 w-[34%]" />
                {["Standard", "Tradecraft", "Full"].map((h, i) => (
                  <th key={h} className={`py-4 px-4 t-head text-2xl relative border-l border-rule ${i === 1 ? "bg-manila/40" : ""}`} data-testid={`pricing-col-${h.toLowerCase()}`}>
                    {h}
                    {i === 1 && <span className="absolute -top-5 left-1/2 -translate-x-1/2"><Stamp color="red" rotate={-6} size="sm" testId="recommended-stamp">Recommended</Stamp></span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r, ri) => (
                <tr key={r[0]} className="border-b border-rule/60">
                  <td className="py-3 pr-4 t-label text-olive">{r[0]}</td>
                  {r.slice(1).map((v, i) => (
                    <td key={i} className={`py-3 px-4 border-l border-rule ${ri === 0 ? "t-head text-3xl" : "t-body text-base"} ${v === "—" ? "text-rule" : ""} ${i === 1 ? "bg-manila/40" : ""}`}>{v}</td>
                  ))}
                </tr>
              ))}
              <tr>
                <td className="py-5 pr-4" />
                {["standard", "tradecraft", "full"].map((t, i) => (
                  <td key={t} className={`py-5 px-4 border-l border-rule ${i === 1 ? "bg-manila/40" : ""}`}>
                    <Link to={`/checkout?tier=${t}`} className={`btn ${i === 1 ? "btn-stamp" : ""}`} data-testid={`select-${t}`}>Select</Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 max-w-[70ch]">
          <Note>Annual prepayment: twelve months for the price of ten. Volume: 3–9 locations 15% · 10–24 locations 25% · 25+ by contract. Billing begins when testing begins, not when you purchase.</Note>
        </div>
      </section>

      <section className="mt-20" data-testid="pricing-section-b">
        <SectionLabel>Section B — Installations</SectionLabel>
        <div className="mt-6 border-t border-carbon">
          {INSTALLS.map(([name, desc, price, interest]) => (
            <div key={name} className="grid sm:grid-cols-12 gap-2 sm:gap-4 py-5 border-b border-rule items-baseline">
              <span className="sm:col-span-4 t-label">{name}</span>
              <span className="sm:col-span-4 t-body text-base text-olive">{desc}</span>
              <span className="sm:col-span-2 t-mono text-base sm:text-right">{price}</span>
              <Link to={`/contact?interest=${interest}`} className="sm:col-span-2 t-label text-carbon hover:text-stamp sm:text-right whitespace-nowrap" data-testid={`memo-${interest}-${name.slice(-6).replace(/\W/g, "")}`}>Request the memorandum →</Link>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-20" data-testid="pricing-section-c">
        <SectionLabel>Section C — Entry</SectionLabel>
        <div className="mt-6 border-t border-carbon">
          <div className="grid sm:grid-cols-12 gap-2 sm:gap-4 py-5 border-b border-rule items-baseline">
            <span className="sm:col-span-4 t-label">The Key Ring Audit</span>
            <span className="sm:col-span-4 t-body text-base text-olive">Ten questions, four minutes</span>
            <span className="sm:col-span-2 t-mono text-base sm:text-right">No charge</span>
            <Link to="/audit" className="sm:col-span-2 t-label text-carbon hover:text-stamp sm:text-right" data-testid="entry-audit-link">Take it →</Link>
          </div>
          <div className="grid sm:grid-cols-12 gap-2 sm:gap-4 py-5 border-b border-rule items-baseline">
            <span className="sm:col-span-4 t-label">The Findings Review</span>
            <div className="sm:col-span-4 t-body text-base text-olive">Ninety minutes, remote, written memorandum<br /><span className="text-sm">Credited in full against any installation booked within ninety days.</span></div>
            <span className="sm:col-span-2 t-mono text-base sm:text-right">$1,500</span>
            <Link to="/checkout?product=findings-review" className="sm:col-span-2 t-label text-carbon hover:text-stamp sm:text-right" data-testid="entry-review-link">Book it →</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
